'use client';
import React, { useState } from 'react';
import { Collapse, ConfigProvider, Flex } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import WaveRightIcon from '../SVG/WaveRightIcon';
import WaveLeftIcon from '../SVG/WaveLeftIcon';
import { WATrackerClickEvn } from '@/utils/wa';
import useWindowSize from '@/utils/useScreenSize';
import { mobileBreakPoint } from '@/utils/utils';
import RichText from '../RichText';

interface Props {
    content: {
        name: string;
        accordionContainerHeader: {
            rendered: string;
        };
        accordionContainerContent: {
            rendered: string;
        };
        accordionContainerDefaultActiveKey: {
            value: string;
        };
        accordionRows: any;
        titleIconOptions?: {
            value: string;
        };
    };
}

/** 适用于 pageDetail-Accordion Row components  */
const PageAccordion = (props: Props) => {
    const { content } = props;
    const [expandKeys, setExpandKeys] = useState(
        [props.content?.accordionContainerDefaultActiveKey.value].filter(Boolean),
    );
    const globalStore = useGlobalStore();
    const themeColor = globalStore.currentTheme?.themeColor;
    const musicWaveColor = globalStore.currentTheme?.musicWaveColor;
    // console.log('Accordion------', props);

    const TITLE_ICON = {
        titleIconRight: () => <WaveRightIcon iconColor={musicWaveColor}></WaveRightIcon>,
        titleIconLeft: () => <WaveLeftIcon iconColor={musicWaveColor}></WaveLeftIcon>,
    };
    const TitleIcon = content.titleIconOptions?.value
        ? TITLE_ICON[content.titleIconOptions?.value as keyof typeof TITLE_ICON]
        : () => null;

    const isMobile = useWindowSize().width < mobileBreakPoint;
    return (
        <div className='flex flex-col items-center'>
            <div className='max-w-[1200px] w-full'>
                {props.content?.accordionContainerHeader?.rendered && (
                    <div>
                        <Flex vertical gap={8} className={'xs:ml-0 xmd:ml-4'}>
                            <RichText
                                html={props.content?.accordionContainerHeader?.rendered || ''}
                            />
                            <TitleIcon />
                        </Flex>
                        <Flex vertical gap={8} className={'xs:ml-0 xmd:ml-4'}>
                            <RichText
                                html={props.content?.accordionContainerContent?.rendered || ''}
                            />
                        </Flex>
                        <div className='w-full h-[1px] bg-[#36536F] mt-6'></div>
                    </div>
                )}
                <ConfigProvider
                    theme={{
                        token: {
                            colorBorder: '#36536F;',
                            colorTextHeading: '#fff',
                            colorText: '#fff',
                        },
                        components: {
                            Collapse: {
                                headerBg: 'transparent',
                                headerPadding: isMobile ? '32px 0px' : '32px 16px',
                                contentBg: 'transparent',
                                contentPadding: isMobile ? '22px 0px' : '22px 16px',
                            },
                        },
                    }}
                >
                    <Collapse
                        defaultActiveKey={[
                            props.content?.accordionContainerDefaultActiveKey.value || '',
                        ]}
                        expandIconPosition={'end'}
                        bordered={false}
                        onChange={(key) => {
                            if (key instanceof Array) {
                                setExpandKeys(key);
                                if (key.length > expandKeys.length) {
                                    WATrackerClickEvn(
                                        `RnE/HW_Click-Expand-${content.name}_${key.filter((i) => expandKeys.every((expandItem) => i !== expandItem))[0]}`,
                                    );
                                }
                            }
                        }}
                        expandIcon={({ isActive }) =>
                            isActive ? (
                                <MinusOutlined style={{ fontSize: 22, color: themeColor }} />
                            ) : (
                                <PlusOutlined style={{ fontSize: 22, color: themeColor }} />
                            )
                        }
                        items={filterData(props.content?.accordionRows)}
                    />
                </ConfigProvider>
            </div>
        </div>
    );
};

const filterData = (items: any) =>
    items &&
    items.map((item: any) => {
        return {
            key: item.name,
            label: <RichText html={item?.accordionRowHeader?.rendered || ''} />,
            children: (
                <RichText
                    className='relative xmd:top-[-20px] top-[-14px]'
                    html={item?.accordionRowContent?.rendered || ''}
                />
            ),
        };
    });

export default PageAccordion;
